import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { fetchAllPages } from './fetch-all-pages';

type PresignedResponse = {
  upload_url: string;
  download_url: string;
  key: string;
};

type EvidenceUpload = {
  id: number;
  user_id: number;
  lesson_id: number | null;
  file_url: string;
  file_name: string;
  file_size: number;
  file_type: string;
  description: string | null;
  uploaded_at: string;
};

type CreateEvidenceInput = {
  lesson_id?: number;
  file_url: string;
  file_name: string;
  file_size: number;
  file_type: string;
  description?: string;
};

const KEYS = {
  all: ['evidence'] as const,
  mine: (lessonId?: number) => [...KEYS.all, 'mine', lessonId] as const,
};

export function useMyEvidence(lessonId?: number) {
  return useQuery({
    queryKey: KEYS.mine(lessonId),
    queryFn: () =>
      fetchAllPages<EvidenceUpload>('/evidence/mine', {
        ...(lessonId && { lesson_id: String(lessonId) }),
      }),
    enabled: lessonId !== undefined && lessonId > 0,
  });
}

export function usePresignedUrl() {
  return useMutation({
    mutationFn: (data: {
      file_name: string;
      file_type: string;
      file_size: number;
      folder: string;
    }) => api.post<PresignedResponse>('/uploads/presigned', data),
  });
}

export function useCreateEvidence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateEvidenceInput) => api.post<EvidenceUpload>('/evidence', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

export function useReviewEvidence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      status,
      review_notes,
    }: {
      id: number;
      status: 'approved' | 'rejected';
      review_notes?: string;
    }) => api.patch<EvidenceUpload>(`/evidence/${id}/review`, { status, review_notes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      qc.invalidateQueries({ queryKey: ['instructor', 'evidence'] });
    },
  });
}
